// @ts-strict-ignore
import { AppStoreType, IntegrationInstallationId, IntegrationType } from '../types';
import * as React from 'react';
import { FormInstance, FormListOperation, FormListProps, Rule, RuleObject } from 'antd/lib/form';
import { FormLayout } from 'antd/lib/form/Form';
import { flatMap, reduce } from 'lodash';
import { NamePath } from 'antd/lib/form/interface';
import Input, { InputProps } from 'antd/lib/input';
import Select, { SelectProps } from 'antd/lib/select';
import Space from 'antd/lib/space';
import Typography from 'antd/lib/typography';
import Divider from 'antd/lib/divider';
import Form from 'antd/lib/form';
import FormItem, { FormItemProps } from 'antd/lib/form/FormItem';
import Switch, { SwitchProps } from 'antd/lib/switch';
import Checkbox, { CheckboxProps } from 'antd/lib/checkbox/Checkbox';
import { InfoCircleOutlined } from '@ant-design/icons';
import { antGrey } from '../components/styles/globalStyles';

export enum FieldType {
    Input,
    Select,
    Custom,
    Switch,
    Checkbox,
    List,
    WrappedGroup,
}

// I get the prop types directly from the possible components
type PossibleFieldProps = SwitchProps & CheckboxProps & FormItemProps & InputProps & SelectProps;

export type ListItemWrapperFunction = (
    children: React.ReactNode,
    listOperations: FormListOperation,
    errors: { errors: React.ReactNode[]; warnings: React.ReactNode[] },
) => React.ReactNode;

export type GroupWrapperFunction = (
    children: React.ReactNode,
    fields: FormFieldData[],
    renderFields: (fields: FormFieldData[]) => React.ReactNode,
) => React.ReactNode;

// then pick only the fields we are actually using
export interface FormFieldData
    extends Pick<
        PossibleFieldProps,
        | 'initialValue'
        | 'name'
        | 'disabled'
        | 'placeholder'
        | 'rules'
        | 'label'
        | 'style'
        | 'dependencies'
        | 'required'
        | 'onChange'
    > {
    fieldType: FieldType;

    data?: any;

    extraData?: any[];

    renderField?: (data: any, ...extraData: any[]) => React.ReactNode; // used for custom fields
    helpMessage?: React.ReactNode;
    tooltip?: React.ReactNode;
    extraFormItemProps?: FormItemProps;
    skipFormItemWrapper?: boolean;
    inputProps?: PossibleFieldProps;
    // Shows the field only while another field in the same form (or release-type tab) has this value.
    visibleWhen?: { field: string; equals: unknown };

    // group specific
    groupedFields?: FormFieldData[];
    groupChildrenWrapper?: GroupWrapperFunction;

    // list specific
    nestedListFields?: FormFieldData[];
    listChildrenWrapper?: ListItemWrapperFunction;

    // switch specific
    switchOnText?: React.ReactNode;
    switchOffText?: React.ReactNode;

    showDivider?: boolean;
}

export const integrationModalContentUsingFormInstance: (
    form: FormInstance,
    layout: FormLayout,
    integrationType: IntegrationType,
    integrationInstallationId: IntegrationInstallationId,
    fields: FormFieldData[],
    onFieldsChange: (integrationType: IntegrationType, form: FormInstance) => void,
    onFinish: (
        integrationInstallationId: IntegrationInstallationId,
        integrationType: IntegrationType,
        form: FormInstance,
    ) => void,
) => React.ReactNode = (form, layout, integrationType, integrationInstallationId, fields, onFieldsChange, onFinish) => {
    const initialValues = {};

    fields.forEach((field) => {
        if (field.fieldType === FieldType.WrappedGroup) {
            field.groupedFields?.forEach((groupedField) => {
                initialValues[groupedField.name] = groupedField.initialValue ?? null;
            });
        } else {
            initialValues[field.name] = field.initialValue ?? null;
        }
    });

    return (
        <Form
            form={form}
            layout={layout}
            autoComplete="off"
            initialValues={initialValues}
            onFinish={() => onFinish(integrationInstallationId, integrationType, form)}
            onFieldsChange={() => onFieldsChange(integrationType, form)}
        >
            {renderFormFields(fields)}
        </Form>
    );
};

export const automationAndNotificationSettingsModalContentUsingFormInstance: (
    form: FormInstance,
    fields: FormFieldData[],
    disableForSubscription: boolean,
    onFinish: (form: FormInstance) => void,
    children?: React.ReactNode,
) => React.ReactNode = (form, fields, disableForSubscription, onFinish, children) => {
    const initialValues = {};

    fields.forEach((field) => {
        if (field.fieldType === FieldType.WrappedGroup) {
            field.groupedFields?.forEach((groupedField) => {
                initialValues[groupedField.name] = groupedField.initialValue ?? null;
            });
        } else {
            initialValues[field.name] = field.initialValue ?? null;
        }
    });

    return (
        <Form
            form={form}
            autoComplete="off"
            layout="vertical"
            initialValues={initialValues}
            onFinish={() => onFinish(form)}
            disabled={disableForSubscription}
        >
            {children ?? renderFormFields(fields)}
        </Form>
    );
};

interface SelectData {
    value: string;
    displayValue: string;
}

export const renderFormField: (
    field: FormFieldData,
    previousField?: FormFieldData,
    nextField?: FormFieldData,
) => React.ReactNode = (field, previousField, nextField) => {
    let innerContent: React.ReactNode;
    let hasFeedback = true;

    switch (field.fieldType) {
        case FieldType.Input:
            innerContent = (
                <Input
                    placeholder={field.placeholder}
                    onChange={field.onChange}
                    allowClear
                    disabled={field.disabled !== undefined ? field.disabled : false}
                    autoComplete="off"
                />
            );
            break;
        case FieldType.Select: {
            const data: SelectData[] = field.data;

            innerContent = (
                <Select
                    placeholder={field.placeholder ?? 'Select'}
                    onChange={field.onChange}
                    disabled={field.disabled !== undefined ? field.disabled : false}
                    {...field.inputProps}
                >
                    {data.map((option, index) => {
                        return (
                            <Select.Option value={option.value} key={index}>
                                {option.displayValue}
                            </Select.Option>
                        );
                    })}
                </Select>
            );
            break;
        }
        case FieldType.Custom: {
            innerContent = field.renderField(field.data, ...(field.extraData ?? []));
            break;
        }
        case FieldType.WrappedGroup: {
            const fields = field.groupedFields ?? [];

            const children = (
                <>
                    {fields?.map((groupedField) => {
                        return (
                            <React.Fragment key={groupedField.name}>{renderFormFields([groupedField])}</React.Fragment>
                        );
                    })}
                </>
            );

            if (field.groupChildrenWrapper) {
                innerContent = field.groupChildrenWrapper(children, fields, renderFormFields);
            } else {
                innerContent = children;
            }

            innerContent = (
                <>
                    {innerContent}
                    {field.helpMessage && <Typography.Text type="secondary">{field.helpMessage}</Typography.Text>}
                </>
            );

            return innerContent;
        }
        case FieldType.List: {
            const nestedListFields = field.nestedListFields ?? [];
            innerContent = (
                <div
                    style={{
                        width: '100%',
                    }}
                >
                    {field.showDivider && <Divider />}
                    <Space
                        direction="vertical"
                        style={{
                            width: '100%',
                        }}
                        size={14}
                    >
                        {field.label && (
                            <Typography.Text
                                style={{
                                    color: antGrey.grey8,
                                    fontSize: 14,
                                }}
                            >
                                <strong>{field.label}</strong>
                            </Typography.Text>
                        )}
                        <Form.List {...(field as Omit<FormListProps, 'children'>)}>
                            {(listFieldsData, listOperations, errors) => {
                                const nestedFieldsNode = listFieldsData.map((listFieldData, index) => {
                                    const nestedFields = nestedListFields.map((nestedFieldData) => {
                                        const name =
                                            nestedFieldData.fieldType === FieldType.Custom ?
                                                [listFieldData.name]
                                            :   [listFieldData.name, nestedFieldData.name];

                                        return {
                                            ...nestedFieldData,
                                            name,
                                            data: {
                                                ...nestedFieldData.data,
                                                listOperations,
                                                index,
                                                listFieldData,
                                            },
                                        };
                                    }) as FormFieldData[];
                                    return renderFormFields(nestedFields);
                                });

                                // if there is a wrapper function, use it
                                if (field.listChildrenWrapper) {
                                    return field.listChildrenWrapper(nestedFieldsNode, listOperations, errors);
                                }

                                return nestedFieldsNode;
                            }}
                        </Form.List>
                    </Space>
                </div>
            );

            return innerContent;
        }
        case FieldType.Switch:
            innerContent = (
                <Switch
                    checkedChildren={field.switchOnText ?? 'On'}
                    onChange={field.onChange}
                    unCheckedChildren={field.switchOffText ?? 'Off'}
                    disabled={field.disabled !== undefined ? field.disabled : false}
                />
            );
            hasFeedback = false;
            break;
        case FieldType.Checkbox:
            innerContent = (
                <Checkbox disabled={field.disabled} onChange={field.onChange}>
                    {field.data}
                </Checkbox>
            );
            hasFeedback = false;
            break;
    }

    const valueProps =
        field.fieldType === FieldType.Switch || field.fieldType === FieldType.Checkbox ?
            { valuePropName: 'checked' }
        :   {};

    const hasRequiredRule = !!field.rules?.find((rule) => !!(rule as RuleObject)?.required);

    const isCheckbox = field.fieldType === FieldType.Checkbox;
    const marginTop = isCheckbox && previousField?.fieldType === FieldType.Checkbox ? 6 : 12;
    const marginBottom = isCheckbox && nextField?.fieldType === FieldType.Checkbox ? 6 : 12;

    return (
        <React.Fragment key={field.name}>
            {field.showDivider && <Divider />}
            {field.skipFormItemWrapper ?
                innerContent
            :   <FormItem
                    key={field.name}
                    name={field.name}
                    label={field.label}
                    tooltip={
                        field.tooltip && {
                            title: field.tooltip,
                            icon: <InfoCircleOutlined style={{ color: antGrey.grey7, marginLeft: 8 }} />,
                        }
                    }
                    rules={field.rules}
                    extra={field.helpMessage}
                    dependencies={field.dependencies}
                    required={field.required || hasRequiredRule}
                    hasFeedback={hasFeedback}
                    style={{ marginTop, marginBottom, ...field.style }}
                    initialValue={field.initialValue}
                    {...valueProps}
                    {...field.extraFormItemProps}
                >
                    {innerContent}
                </FormItem>
            }
        </React.Fragment>
    );
};

export const renderFormFields: (fields: FormFieldData[]) => React.ReactNode = (fields) => {
    return fields.map((field, index) => renderFormField(field, fields[index - 1], fields[index + 1]));
};

export const formValidAndRequiredFieldsNotEmpty: (form: FormInstance, requiredFields: NamePath[]) => boolean = (
    form,
    requiredFields,
) => {
    const errors = flatMap(form.getFieldsError().map((field) => field.errors));
    const allRequiredFieldsValues = form.getFieldsValue(requiredFields);

    const allFieldsHaveValue = reduce(
        allRequiredFieldsValues,
        (prevFieldsHaveValue, fieldValue) => {
            if (prevFieldsHaveValue) {
                return fieldValue !== null && fieldValue !== undefined;
            }

            return prevFieldsHaveValue;
        },
        true,
    );

    return allFieldsHaveValue && errors.length === 0;
};

export const filterOptionLabel = (input, option) => {
    if (!input) {
        return false;
    }
    const search = input.toLowerCase();
    const matchesSearch = (text) =>
        String(text ?? '')
            .toLowerCase()
            .includes(search);
    return matchesSearch(typeof option.label === 'string' ? option.label : '') || matchesSearch(option.value);
};

export const BASE_BRANCH_FIELD_RULES = [
    {
        validator: (_, value) => {
            if (value && value.includes(' ')) {
                return Promise.reject('Branch cannot contain a space');
            }

            return Promise.resolve();
        },
    },
];

const isValidEmail = (email?: string) => {
    const trimmedValue = email?.trim();
    if (!trimmedValue) {
        return false;
    }
    const regex = /^[\w\-\.]+@([\w-]+\.)+[\w-]{2,6}$/;
    return regex.test(trimmedValue);
};

export const emailValidationRules = (emails: string[], required = true) => {
    const rules = [];
    if (required === true) {
        rules.push({ required: true, message: 'At least one email address is required' });
    }
    const enterValidEmailMessage = 'Please enter a valid email';

    rules.push(
        {
            validator: (_, value) => {
                if (Array.isArray(value)) {
                    for (const email of value) {
                        if (!isValidEmail(email)) {
                            return Promise.reject(enterValidEmailMessage);
                        }
                    }
                    return Promise.resolve();
                }

                if (typeof value === 'string') {
                    if (!isValidEmail(value)) {
                        return Promise.reject(enterValidEmailMessage);
                    } else {
                        return Promise.resolve();
                    }
                }
                return Promise.reject(enterValidEmailMessage);
            },
            message: enterValidEmailMessage,
        },
        {
            validator: (_, value) => {
                if (typeof value === 'string' && emails.includes(value)) {
                    return Promise.reject('Email already added');
                } else {
                    return Promise.resolve();
                }
            },
            message: 'Email already added',
        },
    );
    return rules;
};

export const validateFileNameForAppStoreType: (fileName: string, appStoreType: AppStoreType) => [boolean, string] = (
    fileName,
    appStoreType,
) => {
    switch (appStoreType) {
        case AppStoreType.Apple:
            return [fileName.endsWith('.ipa'), 'Artifact filenames must be of type .ipa'];
        case AppStoreType.Google:
        case AppStoreType.Huawei:
        case AppStoreType.Samsung:
            return [
                fileName.endsWith('.aab') || fileName.endsWith('.apk'),
                'Artifact filenames must be of type .aab or .apk',
            ];
        case AppStoreType.Amazon:
            return [fileName.endsWith('.apk'), 'Artifact filenames must be of type .apk'];
        default:
            return [true, 'Artifact filenames must be of the correct type'];
    }
};

export const stagedRolloutPercentageInputValidationRules: Rule[] = [
    {
        validator(_rule, value) {
            if (value === 0 || value >= 100) {
                return Promise.reject();
            }

            return Promise.resolve();
        },
        message:
            'Staged rollout percentage must be greater than 0 and less than 100. Up to 6 decimal points are allowed.',
    },
];

export const filterOptionPrefixMatch = (input, option) => {
    if (!input) return false;
    const text = option?.children?.toString() || option?.label?.toString();
    return text?.toLowerCase().startsWith(input.toLowerCase()) ?? false;
};

export const isValidCommitHashFormat = (hash: string): boolean => {
    const commitHashRegex = /^[0-9a-fA-F]{40}$/;
    return commitHashRegex.test(hash);
};
